import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiList, FiGitCommit } from 'react-icons/fi';
import { api } from '../api';
import TimelineStats from '../components/timeline/TimelineStats';
import TimelineMiniChart from '../components/timeline/TimelineMiniChart';
import TimelineFilters from '../components/timeline/TimelineFilters';
import TimelineView from '../components/timeline/TimelineView';
import TimelineVisual from '../components/timeline/TimelineVisual';
import './Timeline.css';

const INITIAL_FILTERS = {
  company: '',
  sources: [],
  eventTypes: [],
  categories: [],
  dateFrom: '',
  dateTo: '',
};

function Timeline() {
  const { companyName } = useParams();
  const isCompanyView = !!companyName;

  // Data state
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [viewMode, setViewMode] = useState('feed'); // 'feed' | 'visual'

  const LIMIT = 200;
  const VISUAL_LIMIT = 10000; // Load all for visual view — needs full date range

  // Build API params from filters
  const buildParams = useCallback((extraOffset = 0) => {
    const params = {
      limit: viewMode === 'visual' ? VISUAL_LIMIT : LIMIT,
      offset: extraOffset,
      include_unscraped: false,
    };

    // In company view, don't send company filter (it's in the URL)
    if (!isCompanyView && filters.company) {
      params.company = filters.company;
    }

    if (filters.sources.length > 0) {
      params.source = filters.sources.join(',');
    }
    if (filters.eventTypes.length > 0) {
      params.event_type = filters.eventTypes.join(',');
    }
    if (filters.categories.length > 0) {
      params.category = filters.categories.join(',');
    }
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;

    return params;
  }, [filters, isCompanyView, viewMode]);

  // Fetch events
  const fetchEvents = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
      }

      const currentOffset = reset ? 0 : offset;
      const params = buildParams(currentOffset);

      const data = isCompanyView
        ? await api.getCompanyTimeline(companyName, params)
        : await api.getTimelineEvents(params);

      if (reset) {
        setEvents(data.events || []);
      } else {
        setEvents((prev) => [...prev, ...(data.events || [])]);
      }

      setTotalCount(data.total_count || 0);
      setHasMore(data.has_more || false);
    } catch (err) {
      console.error('Failed to fetch timeline events:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams, isCompanyView, companyName, offset]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = isCompanyView ? { company: companyName } : {};
      const data = await api.getTimelineStats(params);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch timeline stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [isCompanyView, companyName]);

  // Initial load + refetch on view mode change
  useEffect(() => {
    fetchEvents(true);
    fetchStats();
  }, [companyName, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on filter change (debounced for company search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEvents(true);
    }, filters.company ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more
  const handleLoadMore = () => {
    setLoadingMore(true);
    const newOffset = offset + LIMIT;
    setOffset(newOffset);

    const params = buildParams(newOffset);
    const fetcher = isCompanyView
      ? api.getCompanyTimeline(companyName, params)
      : api.getTimelineEvents(params);

    fetcher.then((data) => {
      setEvents((prev) => [...prev, ...(data.events || [])]);
      setHasMore(data.has_more || false);
    }).catch((err) => {
      console.error('Failed to load more events:', err);
    }).finally(() => {
      setLoadingMore(false);
    });
  };

  // Filter handlers
  const handleFilterChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
  };

  const hasActiveFilters =
    filters.company ||
    filters.sources.length > 0 ||
    filters.eventTypes.length > 0 ||
    filters.categories.length > 0 ||
    filters.dateFrom ||
    filters.dateTo;

  // Chart bar click → filter to that month
  const handleBarClick = (month) => {
    if (!month) return;
    const [year, m] = month.split('-');
    const lastDay = new Date(parseInt(year), parseInt(m), 0).getDate();
    setFilters((prev) => ({
      ...prev,
      dateFrom: `${month}-01`,
      dateTo: `${month}-${lastDay}`,
    }));
  };

  // Loading skeleton
  const renderSkeleton = () => (
    <div className="timeline-view" style={{ paddingLeft: 40 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="timeline-skeleton-card">
          <div className="timeline-skeleton-line short" />
          <div className="timeline-skeleton-line long" />
          <div className="timeline-skeleton-line medium" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="timeline-page">
      {/* Header */}
      <div className="timeline-header">
        <div className="timeline-header-content">
          <div className="timeline-header-left">
            {isCompanyView && (
              <Link to="/timeline" className="timeline-back-link">
                <FiArrowLeft size={14} />
                All Companies
              </Link>
            )}
            <h1 className="timeline-title">
              {isCompanyView ? companyName : 'Portfolio Timeline'}
            </h1>
            <p className="timeline-subtitle">
              {isCompanyView
                ? 'Chronological intelligence from all data sources'
                : `${totalCount.toLocaleString()} events across ${stats?.companies_covered || '...'} portfolio companies`
              }
            </p>
            {/* View toggle */}
            {!isCompanyView && (
              <div className="timeline-view-toggle">
                <button
                  className={`timeline-view-tab ${viewMode === 'feed' ? 'active' : ''}`}
                  onClick={() => setViewMode('feed')}
                >
                  <FiList size={14} /> Feed
                </button>
                <button
                  className={`timeline-view-tab ${viewMode === 'visual' ? 'active' : ''}`}
                  onClick={() => setViewMode('visual')}
                >
                  <FiGitCommit size={14} /> Visual
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <TimelineStats stats={stats} loading={statsLoading} />

      {/* Mini Chart */}
      {stats?.by_month && (
        <TimelineMiniChart byMonth={stats.by_month} onBarClick={handleBarClick} />
      )}

      {/* Filters */}
      <TimelineFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        showCompanySearch={!isCompanyView}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Timeline */}
      {loading ? (
        renderSkeleton()
      ) : viewMode === 'visual' && !isCompanyView ? (
        <TimelineVisual events={events} />
      ) : (
        <>
          <TimelineView
            events={events}
            showCompany={!isCompanyView}
            onEventUpdated={(updated) => {
              setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
            }}
          />

          {hasMore && (
            <div className="timeline-load-more">
              <button
                className="timeline-load-more-btn"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : `Load More (${totalCount - events.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Timeline;
